import { Link } from "react-router-dom";
import { ScrollText, MapPin, Phone, MessageCircle } from "lucide-react";
import { WHATSAPP_URL, PHONE_NUMBER, ADDRESS, DISCLAIMER_TEXT } from "@/lib/pdf";

const LEGAL_LINKS = [
  { to: "/privacy-policy", label: "Privacy Policy", testId: "footer-link-privacy" },
  { to: "/terms", label: "Terms & Conditions", testId: "footer-link-terms" },
  { to: "/refund-policy", label: "Refund Policy", testId: "footer-link-refund" },
  { to: "/contact", label: "Contact Us", testId: "footer-link-contact" },
  { to: "/disclaimer", label: "Disclaimer", testId: "footer-link-disclaimer" },
];

export default function Footer() {
  return (
    <footer className="bg-navy text-white" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white/5 text-gold">
                <ScrollText size={18} />
              </span>
              <div className="leading-tight">
                <div className="font-serif text-xl font-semibold">WillSeva</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-gold">Your Family. Your Legacy. Protected.</div>
              </div>
            </div>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">
              A premium Indian will-writing service. Our legal consultants are senior
              advocates specialising exclusively in succession, testamentary and estate
              planning law — ensuring every Will meets the highest legal standard.
            </p>
          </div>

          <div>
            <h4 className="font-serif text-base text-gold">Navigate</h4>
            <div className="mt-4 gold-divider" />
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/" className="text-white/80 hover:text-gold" data-testid="footer-link-home">Home</Link></li>
              <li><Link to="/will-generator" className="text-white/80 hover:text-gold" data-testid="footer-link-will-generator">Will Generator</Link></li>
              <li><Link to="/pricing" className="text-white/80 hover:text-gold" data-testid="footer-link-pricing">Pricing</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-gold" data-testid="footer-link-contact-nav">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-base text-gold">Reach Us</h4>
            <div className="mt-4 gold-divider" />
            <ul className="mt-4 space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 text-gold" />
                <span data-testid="footer-address">{ADDRESS}</span>
              </li>
              <li className="flex items-start gap-2">
                <MessageCircle size={16} className="mt-0.5 text-gold" />
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-gold"
                  data-testid="footer-whatsapp-link"
                >
                  WhatsApp {PHONE_NUMBER}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={16} className="mt-0.5 text-gold" />
                <a href="tel:+919167249612" className="hover:text-gold" data-testid="footer-phone-link">
                  {PHONE_NUMBER}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-8">
          <h4 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
            Legal & Compliance
          </h4>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {LEGAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-white/75 hover:text-gold"
                  data-testid={l.testId}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 rounded-md border border-white/10 bg-white/[0.03] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Disclaimer</div>
          <p className="mt-2 text-xs leading-relaxed text-white/70" data-testid="footer-disclaimer">
            {DISCLAIMER_TEXT}
          </p>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row sm:items-center">
          <div>© {new Date().getFullYear()} WillSeva. All rights reserved.</div>
          <div className="font-serif italic text-gold/90">"Seva first. Law always."</div>
        </div>
      </div>
    </footer>
  );
}
